import { Map } from "@iventis/mapbox-gl";
import { AnySupportedGeometry, MapObjectProperties } from "@iventis/map-types";
import { Feature, Point, Polygon } from "geojson";
import { BehaviorSubject } from "rxjs";
import { AreaModelStyle } from "@iventis/domain-model/model/areaModelStyle";
import { AreaStyle } from "@iventis/domain-model/model/areaStyle";
import { getStaticStyleValue } from "@iventis/layer-style-helpers";
import { AreaModelDistribution } from "@iventis/domain-model/model/areaModelDistribution";
import { v4 as uuid } from "uuid";
import { AreaModelRotation } from "@iventis/domain-model/model/areaModelRotation";
import { getMapObjectQuantityOfModelsInAreaSystemDataField, getMapObjectRotationZOfModelsInAreaSystemDataField } from "@iventis/datafields";
import { ModelDataStore } from "../../data-store/model-data-store";
import { LayerStorageScope, MapModuleLayer, MapState } from "../../types/store-schema";
import { StylePropertyToValueMapV2 } from "../../types/style-types";
import { MapboxModelBaseLayer } from "./mapbox-model-base-layer";
import { MapboxAreaBaseLayer } from "./mapbox-area-base-layer";
import { removeLayerFilterByTypeAndId, updateLayerFilters } from "../mapbox/filters/engine-mapbox-filter-helpers";
import { FilterType } from "../mapbox/filters/engine-mapbox-filter-types";
import { removeAdditionalLayerSuffixes } from "../../utilities/layer.helpers";
import { Layer } from "../generic-layers/layer";

import { generateSeededRandomPointsWithinPolygon, generateUniformGridPointsWithinPolygon } from "../../utilities/points-within-area-geojson";
import { getSeededRandomDegreesFromCoordinate } from "../../utilities/seeded-coordinate-random";

/** Maps area model style properties to their area style equivalent */
const areaModelStyleToAreaStyleMapping = {
    areaColour: "colour",
    areaOpacity: "opacity",
};

function getAreaPointLayerId(layerId: string) {
    return `${layerId}_area_points`;
}

export class MapboxAreaModelLayer extends Layer<Feature<Polygon, MapObjectProperties>[], AreaModelStyle> {
    public readonly layerId: string;

    public readonly sourceId: string;

    private removed = false;

    private areaLayer: MapboxAreaBaseLayer;

    private modelLayer: MapboxModelBaseLayer<Feature<AnySupportedGeometry, MapObjectProperties>[], AreaModelStyle>;

    private hasOutline = false;

    /** Holds the deleted map object ids, for the model layer filter */
    private deletedMapObjectIds: string[] = [];

    private readonly styleChangesAffectingModelPositioning: (keyof AreaModelStyle)[] = ["rotationType", "distributionType"];

    private readonly styleChangesAffectingAreaStyle: (keyof AreaModelStyle)[] = ["areaColour", "areaOpacity"];

    public get layerAddedToMap() {
        return this.modelLayer.layerAddedToMap;
    }

    constructor(
        private readonly map: Map,
        layer: MapModuleLayer,
        state$: BehaviorSubject<MapState>,
        modelDataStore: ModelDataStore,
        config: {
            getAboveLayerId: (layerId: string) => string;
        } & Record<string, unknown>
    ) {
        super(state$);
        this.layerId = layer.id;
        this.sourceId = layer.id;
        const modelLayerId = getAreaPointLayerId(layer.id);
        const modelLayer = { ...layer, id: modelLayerId, source: modelLayerId };
        this.modelLayer = new MapboxModelBaseLayer<Feature<AnySupportedGeometry, MapObjectProperties>[], AreaModelStyle>(
            map,
            modelLayer,
            state$,
            modelDataStore,
            { name: layer.name, type: "model" },
            {
                getAboveLayerId: () => config.getAboveLayerId(removeAdditionalLayerSuffixes(layer.id)),
            }
        );
        this.modelLayer.getIventisLayer = () => ({ ...this.getIventisLayer(), id: modelLayerId, source: modelLayerId, storageScope: LayerStorageScope.LocalOnly });

        this.areaLayer = new MapboxAreaBaseLayer(map, layer, state$, {
            getAboveLayerId: () => modelLayerId,
            getStyle: (layer) => ({ colour: layer.areaModelStyle.areaColour, opacity: layer.areaModelStyle.areaOpacity }),
        });
    }

    public async initaliseLayer(layer: MapModuleLayer) {
        await this.modelLayer.initaliseLayer(layer);

        // Check if the layer has been removed since we initalised the model layer
        if (this.removed) {
            return;
        }

        this.areaLayer.addLayer();
        const features = this.getGeoJson();
        if (features != null) {
            this.updateLocalSource(features);
        }
    }

    public addLayer(): void {
        if (!this.modelLayer.hasLocalLayer) {
            this.modelLayer.addLocalSource();
        }
        if (!this.layerAddedToMap) {
            this.modelLayer.addLayer("local");
        }
    }

    public removeLayer(): void {
        this.modelLayer.removeLayer();
        this.areaLayer.removeLayer();
        this.removed = true;
    }

    public addLocalSource(): void {
        this.modelLayer.addLocalSource();
        this.areaLayer.addLocalSource();
    }

    public updateLocalSource(source: Feature<Polygon, MapObjectProperties>[], drawingSource?: Feature<Polygon, MapObjectProperties>[]): void {
        if (!this.layerAddedToMap) {
            return;
        }

        // If drawing source is populated then we are drawing and don't need to update the model positions
        if (drawingSource == null) {
            const layer = this.getIventisLayer();
            const distribution = getStaticStyleValue(layer.areaModelStyle.distributionType);
            const rotationType = getStaticStyleValue(layer.areaModelStyle.rotationType);

            const quantityDataFieldId = getMapObjectQuantityOfModelsInAreaSystemDataField(layer.dataFields)?.id;
            const rotationDataFieldId = getMapObjectRotationZOfModelsInAreaSystemDataField(layer.dataFields)?.id;
            const allModelPoints: Feature<Point, MapObjectProperties>[] = [];

            for (const feature of source) {
                const quantity = quantityDataFieldId ? (feature.properties[quantityDataFieldId] as number) ?? 1 : 1;
                const rotation = rotationDataFieldId ? (feature.properties[rotationDataFieldId] as number) ?? 0 : 0;
                const modelPositions =
                    distribution === AreaModelDistribution.Random
                        ? generateSeededRandomPointsWithinPolygon(feature, quantity)
                        : generateUniformGridPointsWithinPolygon(feature, quantity);

                for (const modelPosition of modelPositions) {
                    const rotationValue =
                        rotationType === AreaModelRotation.Random ? getSeededRandomDegreesFromCoordinate(modelPosition.geometry.coordinates as [number, number]) : rotation;
                    modelPosition.properties = { ...feature.properties, baseMapObjectId: feature.properties.id, id: uuid(), rotationZ: rotationValue };
                    allModelPoints.push(modelPosition as Feature<Point, MapObjectProperties>);
                }
            }

            this.modelLayer.updateLocalSource(allModelPoints);
        }

        // If an outline is present, update the outline source
        if (this.hasOutline && drawingSource) {
            this.areaLayer.outlineLayer.updateLocalSource(
                drawingSource.map((feature) => ({ ...feature, geometry: { type: "LineString", coordinates: feature.geometry.coordinates[0] } }))
            );
        }
        this.areaLayer.updateLocalSource(source);
    }

    public removeLocalSource(): void {
        this.modelLayer.removeLocalSource();
        this.areaLayer.removeLocalSource();
    }

    public hideLayer(): void {
        this.modelLayer.hideLayer();
        this.areaLayer.hideLayer();
    }

    public showLayer(): void {
        this.modelLayer.showLayer();
        this.areaLayer.showLayer();
    }

    public updateMapLevel(level: number): void {
        this.modelLayer.updateMapLevel(level);
        this.areaLayer.updateMapLevel(level);
    }

    public updateMapOrder(aboveLayerId: string): void {
        this.modelLayer.updateMapOrder(aboveLayerId);
        this.areaLayer.updateMapOrder(this.modelLayer.layerId);
    }

    public addDateFilter() {
        const layer = this.getIventisLayer();
        const dateFilter = this.state$.value.datesFilter.value;
        if (dateFilter.filter) {
            this.modelLayer.addDateFilter(layer.dataFields, dateFilter.day, dateFilter.time);
            this.areaLayer.addDateFilter();
        }
    }

    public removeDateFilter() {
        this.modelLayer.removeDateFilter();
        this.areaLayer.removeDateFilter();
    }

    public async updateStyle(styleChanges: StylePropertyToValueMapV2<AreaModelStyle>): Promise<void> {
        const areaStyleChanges: Partial<AreaStyle> = {};
        const modelStyleChanges: Partial<AreaModelStyle> = {};

        let updatePositionOfModels = false;

        for (const [styleProperty, value] of Object.entries(styleChanges)) {
            if (this.styleChangesAffectingAreaStyle.some((positionalChange) => positionalChange === styleProperty)) {
                areaStyleChanges[areaModelStyleToAreaStyleMapping[styleProperty]] = value;
            } else {
                // If these rotationType or distributionType change then we need to update the source of the model layer
                if (this.styleChangesAffectingModelPositioning.includes(styleProperty as keyof AreaModelStyle)) {
                    updatePositionOfModels = true;
                }
                modelStyleChanges[styleProperty] = value;
            }
        }
        await this.modelLayer.updateStyle(modelStyleChanges);
        await this.areaLayer.updateStyle(areaStyleChanges);

        if (updatePositionOfModels) {
            const layerGeoJson = this.getGeoJson();
            if (layerGeoJson != null) {
                this.updateLocalSource(layerGeoJson);
            }
        }
    }

    public getLayerIdsForSnapping(): string[] {
        return this.areaLayer.getLayerIdsForSnapping();
    }

    public onDrawingStarted(objectIds: string[]) {
        super.onDrawingStarted(objectIds);
        this.hasOutline = this.getIventisLayer()?.areaModelStyle?.areaOpacity?.staticValue?.staticValue === 0;
        // If the layer is not yet added to the map, there's no need to hide things or add the outline
        if (this.layerAddedToMap) {
            // Need to pass the deleted map object ids to ensure they stay filtered
            this.hideModelsOnObjects([...objectIds, ...this.deletedMapObjectIds]);
            this.areaLayer.onDrawingStarted(objectIds);
            if (this.hasOutline) {
                this.areaLayer.outlineLayer.addLocalSource();
            }
        }
    }

    public onDrawingFinished(objectIds: string[]) {
        super.onDrawingFinished(objectIds);
        this.unhideModelsOnObjects(objectIds);
        this.areaLayer.outlineLayer.updateLocalSource([]);
        this.hasOutline = false;
    }

    public highlightObjects(objectIds: string[]): void {
        this.areaLayer.highlightObjects(objectIds);
    }

    public removeHighlights(): void {
        this.areaLayer.removeHighlights();
    }

    public getBottomMostLayerId() {
        if (!this.layerAddedToMap) {
            return undefined;
        }

        // Outline is added dynamically so check if it exists first
        const outlineId = this.areaLayer.outlineLayer.layerId;
        if (this.map.getLayer(outlineId)) {
            return outlineId;
        }

        // Ensure the area has been added
        const areaLayerId = this.areaLayer.layerId;
        if (this.map.getLayer(areaLayerId)) {
            return areaLayerId;
        }

        return undefined;
    }

    private hideModelsOnObjects(objectIds: string[]) {
        const currentFilter = this.map.getFilter(this.modelLayer.layerId);
        const modelFilters = objectIds.map((objectId) => ["!=", ["get", "baseMapObjectId"], objectId]);
        const updatedFilter = updateLayerFilters(currentFilter, ...modelFilters);
        this.map.setFilter(this.modelLayer.layerId, updatedFilter);
    }

    private unhideModelsOnObjects(objectIds: string[]) {
        const currentFilter = this.map.getFilter(this.modelLayer.layerId);
        // Remove the filter for all map objects
        const updatedFilter = removeLayerFilterByTypeAndId(currentFilter, FilterType.AreaModels, objectIds);
        this.map.setFilter(this.modelLayer.layerId, updatedFilter);
    }
}
